import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Badge, Pill } from '@/ui/primitives'
import { DataTable, type Coloana } from '@/ui/data-table'
import { NewMenu } from '@/ui/new-menu'
import { eDatorie, lei } from '@/lib/money'

type Miscare = {
  nr: string; client: string; serviciu: string | null; dest: string | null; hotel: string | null
  data: string | null; retur: string | null; val: number | null; rest: number | null
  rest_ron: number | null; mon: string | null; agent: string | null; tel: string | null
  pax: number | null
}
type Lista = { plecari: Miscare[]; sosiri: Miscare[]; eur_rate: number; data: string }

const zi = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('ro-RO', { weekday: 'short', day: '2-digit', month: 'short' }) : '—'

export function Departures() {
  const [tab, setTab] = useState<'plecari' | 'sosiri'>('plecari')
  const { data, isPending } = useQuery({ queryKey: ['plecari'], queryFn: () => api.get<Lista>('/pwa/plecari') })
  const rows = (tab === 'plecari' ? data?.plecari : data?.sosiri) ?? []

  const coloane: Coloana<Miscare>[] = [
    { cheie: 'data', antet: 'Data', sortabil: true, valoare: (r) => r.data ?? '',
      randare: (r) => (
        <span className="tnum text-[13px] whitespace-nowrap">
          {zi(r.data)}
          {r.retur && <span className="block text-[12px] text-muted">retur {zi(r.retur)}</span>}
        </span>
      ) },
    { cheie: 'client', antet: 'Client', sortabil: true, valoare: (r) => r.client,
      randare: (r) => (
        <span>
          <span className="font-medium">{r.client}</span>
          <span className="ml-2 text-[12px] text-muted tnum">{r.nr}</span>
          {r.tel && <span className="block text-[12px] text-muted">{r.tel}</span>}
        </span>
      ) },
    { cheie: 'serviciu', antet: 'Serviciu', sortabil: true, valoare: (r) => r.hotel ?? r.serviciu ?? '',
      randare: (r) => (
        <span className="block max-w-[300px] truncate text-ink-soft">
          {r.hotel ?? r.serviciu ?? '—'}
          {r.dest && <span className="text-muted"> · {r.dest}</span>}
        </span>
      ) },
    { cheie: 'agent', antet: 'Agent', sortabil: true, valoare: (r) => r.agent ?? '',
      randare: (r) => <span className="block max-w-[140px] truncate text-[13px] text-muted">{r.agent ?? '—'}</span> },
    { cheie: 'rest', antet: 'Rest', sortabil: true, aliniat: 'dreapta', valoare: (r) => r.rest_ron ?? 0,
      randare: (r) => (eDatorie(r.rest_ron ?? 0)
        ? <span className="tnum text-bad">{lei(r.rest_ron ?? 0)} lei</span>
        : <Badge tone="good">achitat</Badge>) },
  ]

  return (
    <>
      <PageHeader
        title="Plecari si sosiri"
        count={data ? `${data.plecari.length} plecari · ${data.sosiri.length} sosiri` : undefined}
        actions={<NewMenu />}
      />
      <div className="px-8 py-6 space-y-5">
        <div className="flex gap-2">
          <Pill active={tab === 'plecari'} onClick={() => setTab('plecari')} count={data?.plecari.length}>Plecari</Pill>
          <Pill active={tab === 'sosiri'} onClick={() => setTab('sosiri')} count={data?.sosiri.length}>Sosiri</Pill>
        </div>

        <DataTable
          randuri={rows} coloane={coloane} cheie={(r) => `${r.nr}-${r.data}`} incarca={isPending}
          sortImplicit={{ cheie: 'data', sens: 'asc' }}
          cauta={(r, t) => `${r.nr} ${r.client} ${r.hotel ?? ''} ${r.serviciu ?? ''} ${r.dest ?? ''}`.toLowerCase().includes(t)}
          placeholderCautare="Client, numar, destinatie…"
          gol={{ titlu: tab === 'plecari' ? 'Nicio plecare' : 'Nicio sosire' }}
        />
      </div>
    </>
  )
}
